/**
 * Route Error Component
 *
 * Per-route error UI for TanStack Router's errorComponent.
 * Shown when a route's loader throws or a component crashes.
 * Delegates to purpose-built pages for 401, 403, and 404 errors; anything
 * else gets the shared PageError panel (Alert + Retry). Admin child routes
 * render it `inFrame`; the router default is full-screen.
 */

import { useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { toAppError } from '../../utils/errorUtils';
import { loadFailedTitle } from '@/components/inputs';
import { NotFoundPage } from './NotFoundPage';
import { ForbiddenPage } from './ForbiddenPage';
import { PageError } from './PageError';

interface RouteErrorComponentProps {
  error: Error;
  reset?: () => void;
  /** Rendered in the admin outlet: the header and sidebar stay around it.
   * Otherwise (routes outside the admin frame) it fills the screen. */
  inFrame?: boolean;
}

/**
 * A loader/component inside an authenticated route threw UNAUTHORIZED (the
 * session died mid-session). Rather than render a dead-end "Authentication
 * required" panel — which strands the user behind the admin chrome while the
 * axios 401 interceptor keeps firing the session-expired toast — redirect
 * straight to the login page. `/admin/login` sits outside the admin layout, so
 * navigating there unmounts the chrome (and its toast listener) and ends the
 * loop. The interceptor already performs this redirect for live 401s; this
 * covers the loader-thrown path.
 */
function RedirectToLogin() {
  const router = useRouter();
  useEffect(() => {
    router.navigate({ to: '/admin/login' });
  }, [router]);
  return null;
}

export function RouteErrorComponent({ error, reset, inFrame = false }: RouteErrorComponentProps) {
  const router = useRouter();
  const appError = toAppError(error);

  if (appError.code === 'NOT_FOUND') return <NotFoundPage />;
  if (appError.code === 'UNAUTHORIZED') return <RedirectToLogin />;
  if (appError.code === 'FORBIDDEN') return <ForbiddenPage />;

  // invalidate() re-runs the route's loaders and, by changing the router's
  // loadedAt key, resets the route's error boundary; reset() clears a
  // render crash straight away.
  const handleRetry = () => {
    reset?.();
    void router.invalidate();
  };

  return (
    <PageError
      error={error}
      title={loadFailedTitle('this page')}
      onRetry={handleRetry}
      onGoHome={() => router.navigate({ to: '/admin' })}
      details={error.message}
      fullScreen={!inFrame}
    />
  );
}
